/**
 * One Analytics colour as it is stored everywhere a user can pick one: a { light, dark } pair of
 * six-digit hex values, the light one painted in light mode and the dark one in dark mode. This
 * file is the one place that says what a pair is: the theme and page style validators, the panel
 * style block and the tool schemas all read it from here.
 */
import { isAnalyticsHexColor, toAnalyticsSixDigitHex } from './analyticsSeriesPalette.js';

const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);

/* One colour slot: null (inherit) or a { light, dark } pair, both valid hex. `path` names the slot
   being checked ('seriesColors[3]', 'statusColors.warning', 'style.seriesColors["Gryffindor"]'),
   so a bad value in one slot never reads as "the whole option is wrong".

   An accepted colour is stored in its six digit lowercase form whatever the writer sent: storage
   holding both '#abc' and '#aabbcc' for one colour would leave two colours that look identical
   comparing as different, and a map style expression cannot parse the short form at all. The
   editor already normalizes what a user types; doing it here covers the REST writers too. */
export const validateAnalyticsColorPair = (entry, path) => {
  if (entry === null) {
    return { ok: true, value: null };
  }

  if (!isPlainObject(entry)) {
    return { ok: false, message: `${path} must be null or a {light, dark} hex pair.` };
  }

  const entryKeys = Object.keys(entry);

  if (entryKeys.length !== 2 || !('light' in entry) || !('dark' in entry)) {
    return { ok: false, message: `${path} must have exactly "light" and "dark" keys.` };
  }

  if (!isAnalyticsHexColor(entry.light)) {
    return { ok: false, message: `${path}.light must be a hex colour like #2563eb` };
  }

  if (!isAnalyticsHexColor(entry.dark)) {
    return { ok: false, message: `${path}.dark must be a hex colour like #2563eb` };
  }

  return { ok: true, value: { light: toAnalyticsSixDigitHex(entry.light), dark: toAnalyticsSixDigitHex(entry.dark) } };
};

/* Render-side reading of a stored pair: the hex for one scheme, or an empty string when the stored
   value is not a usable pair. Tolerant on purpose, a stored config is never a reason to fail a
   render. */
export const toAnalyticsColorPairHex = (pair, scheme = 'light') => {
  const hex = isPlainObject(pair) ? pair[scheme === 'dark' ? 'dark' : 'light'] : null;

  return isAnalyticsHexColor(hex) ? toAnalyticsSixDigitHex(hex) : '';
};

/* The pair as every tool that writes one declares it to the model. Nullable because under strict
   mode the model must send a value for a property it names, and null is how it clears one. */
export const ANALYTICS_COLOR_PAIR_TOOL_SCHEMA = {
  type: ['object', 'null'],
  additionalProperties: false,
  required: ['light', 'dark'],
  properties: {
    light: { type: 'string', description: 'Six-digit hex like #2563eb, painted when the viewer is in light mode.' },
    dark: { type: 'string', description: 'Six-digit hex like #60a5fa, painted when the viewer is in dark mode. Pick a lighter or more saturated twin so it reads on a dark ground.' }
  }
};
